import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { of } from 'rxjs';
import { take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DynamicFormComponent } from '../../../core/shared/dynamic-form/dynamic-form.component';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../core/shared/models/picklist.model';
import { OnboradingService } from '../shared/service/onboarding.service';
import { Setup } from '../../setup/shared/service/setup';
import { BackButton } from '../../shared/buttons/back-button';
import { LeadService } from '../../leads/shared/service/leads.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-corporate',
  imports: [CommonModule, DynamicFormComponent, BackButton, TranslatePipe],
  templateUrl: './corporate.html',
})
export class Corporate implements OnInit, AfterViewInit {
  @ViewChild(DynamicFormComponent) dynamicForm!: DynamicFormComponent;

  fields: DynamicFieldConfig[] = [];
  corporateData: any = null;
  viewInitialized = false;
  isViewMode = false;
  selectedClientSeq: number | null = null;
  corporateSeq: number | null = null;
  states: any[] = [];
  cities: any[] = [];
  isTaskView = false;
taskEntitySeq: number | null = null;
  countrySeq = 1;
  institutionNameLocked = false;
  constructor(
    private picklist: PickListService,
    private onboarding: OnboradingService,
    private setup: Setup,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private leadService: LeadService,
    private translate: TranslateService,
  ) {}

  // ================= INIT =================
 ngOnInit(): void {
  const routeId = Number(
    this.route.snapshot.paramMap.get('id'),
  );

  const queryParams =
    this.route.snapshot.queryParamMap;

  this.isViewMode =
    queryParams.get('mode') === 'view';

  this.isTaskView =
    queryParams.get('fromTask') === 'true';

  const clientId = Number(
    queryParams.get('clientId'),
  );

  if (this.isTaskView) {
    // Route ID is entySeq when opened from task.
    this.taskEntitySeq =
      routeId > 0 ? routeId : null;

    this.selectedClientSeq =
      clientId > 0 ? clientId : null;
  } else {
    // Normal route ID is client sequence.
    this.selectedClientSeq =
      clientId > 0
        ? clientId
        : routeId > 0
          ? routeId
          : null;
  }

  this.loadPicklists();
}
  loadInstitutionNameFromClient(clientSeq: number): void {
    this.setup.getclientList().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res.data || [];
        const client = list.find((c: any) => Number(c.clientSeq) === Number(clientSeq));

        if (!client?.clntNm) return;

        const institutionName = client.clntNm.trim();

        // ✅ Wait until dynamic form is ready, then patch + disable
        setTimeout(() => {
          if (!this.dynamicForm?.form) return;

          this.dynamicForm.form.patchValue({
            nmOfInstitution: institutionName,
          });

          this.dynamicForm.form.get('nmOfInstitution')?.disable();
          this.institutionNameLocked = true;
        }, 200);
      },
      error: () => console.error('Failed to load client name'),
    });
  }
  // ================= AFTER VIEW INIT =================
  ngAfterViewInit(): void {
    this.viewInitialized = true;

    if (this.corporateData) {
      this.patchForm(this.corporateData);
    }
  }

  // ================= PICKLIST =================
  loadPicklists(): void {
    this.picklist.loadCodes([
      PKL_GRP.LEGAL_STATUS,
      PKL_GRP.GET_COMPANY,
      PKL_GRP.RESIDENTIAL_STATUS,
      PKL_GRP.TYPE_OF_NBFCS,
    ]);

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.loadStates();
    });
  }
  loadLeadById(leadSeq: number): void {
    this.leadService.getLeadById(leadSeq).subscribe({
      next: (res: any) => {
        const mapped = this.mapResponseToForm(res);

        setTimeout(() => {
          this.dynamicForm.form.patchValue(mapped);
          this.leadService.setLeadForm(mapped);

          // ✅ disable AFTER patch
          if (this.isViewMode) {
            this.dynamicForm.form.disable();
          }
        });
      },
    });
  }
  mapResponseToForm(res: any): any {
    return {
      clientName: res?.clntNm || '',
    };
  }
  // ================= STATES =================
  loadStates(): void {
    this.setup.getStates(this.countrySeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : [];
        this.states = data.map((s: any) => ({
          label: s.stNm,
          value: s.stSeq,
        }));
        this.loadCities();
      },
      error: () => (this.states = []),
    });
  }

  // ================= CITIES =================
  loadCities(): void {
  this.setup.getCity().subscribe({
    next: (res: any) => {
      const data = Array.isArray(res)
        ? res
        : [];

      this.cities = data.map((c: any) => ({
        label: c.cityNm,
        value: c.citySeq,
      }));

      this.buildFields();

      // Opened through dashboard task.
      if (
        this.isTaskView &&
        this.taskEntitySeq
      ) {
        this.loadCorporateFromTask(
          this.taskEntitySeq,
        );

        return;
      }

      // Normal onboarding flow.
      if (this.selectedClientSeq) {
        this.loadCorporate(
          this.selectedClientSeq,
        );

        this.loadInstitutionNameFromClient(
          this.selectedClientSeq,
        );
      }
    },
    error: () => {
      this.cities = [];
    },
  });
}

loadCorporateFromTask(
  entySeq: number,
): void {
  this.onboarding
    .getCorporateInformationByEntitySeq(entySeq)
    .subscribe({
      next: (response: any) => {
        const responseData =
          response?.data ?? response;

        const corporateData =
          Array.isArray(responseData)
            ? responseData[0]
            : responseData;

        if (!corporateData) {
          this.toastr.error(
            this.translate.instant('ONB_CORPORATE.CORPORATE_INFO_NOT_FOUND'),
          );

          return;
        }

        this.corporateData = corporateData;

        this.corporateSeq =
          Number(
            corporateData.crptOnbrdngSeq ??
            corporateData.entySeq ??
            entySeq,
          ) || null;

        const responseClientSeq = Number(
          corporateData.clntSeq ??
          corporateData.clientSeq,
        );

        if (responseClientSeq > 0) {
          this.selectedClientSeq =
            responseClientSeq;
        }

        if (this.corporateSeq) {
          this.onboarding.setCorporateSeq(
            this.corporateSeq,
          );
        }

        if (this.selectedClientSeq) {
          this.onboarding.setClientSeq(
            this.selectedClientSeq,
          );
        }

        if (this.viewInitialized) {
          this.patchForm(corporateData);
        }
      },
      error: (error) => {
        console.error(
          'Task corporate information error:',
          error,
        );

        this.toastr.error(
          this.translate.instant('ONB_CORPORATE.FAILED_TO_LOAD_CORPORATE_INFO'),
        );
      },
    });
}

  // In loadCorporate's next handler — after setting corporateSeq:
  loadCorporate(id: number): void {
    this.onboarding.getCorporateById(id).subscribe({
      next: (res: any) => {
        if (!res) return;

        this.corporateData = res;
        this.corporateSeq = res.crptOnbrdngSeq;

        // ✅ publish to context so parent tabs can use it
        if (this.corporateSeq) {
          this.onboarding.setCorporateSeq(this.corporateSeq);
        }
        if (this.selectedClientSeq) {
          this.onboarding.setClientSeq(this.selectedClientSeq);
        }

        if (this.viewInitialized) {
          this.patchForm(res);
        }
      },
      error: () => this.toastr.error(this.translate.instant('ONB_CORPORATE.FAILED_TO_LOAD_CORPORATE_DATA')),
    });
  }

  // In onFormSubmit's success handler — after setting corporateSeq:
  onFormSubmit(form: any): void {
    if (this.isViewMode) return;

    if (!this.selectedClientSeq) {
      this.toastr.error(this.translate.instant('ONB_CLIENT_LIST.CLIENT_ID_MISSING'));
      return;
    }

    if (form.typ1 === null || form.typ1 === undefined || form.typ1 === '' || form.typ1 === 'null') {
      this.toastr.error(this.translate.instant('ONB_CORPORATE.PLEASE_SELECT_TYPE_1'));
      return;
    }

    if (form.typ2 === null || form.typ2 === undefined || form.typ2 === '' || form.typ2 === 'null') {
      this.toastr.error(this.translate.instant('ONB_CORPORATE.PLEASE_SELECT_TYPE_2'));
      return;
    }

    const payload = this.mapPayload(form);

    this.onboarding.saveCorprate(payload).subscribe({
      next: (res: any) => {
        // Backend validation (e.g. duplicate Incorporation/Reg No., NTN or
        // License Number) comes back as 200 with an error body. ErrorInterceptor
        // already toasts that message and tags the body `__hasError`.
        if (res?.__hasError || res?.success === false) {
          return;
        }

        if (res?.crptOnbrdngSeq) {
          this.corporateSeq = res.crptOnbrdngSeq;

          // ✅ publish IDs to context
          this.onboarding.setCorporateSeq(this.corporateSeq);
          this.onboarding.setClientSeq(this.selectedClientSeq);

          this.router.navigate(['/onboarding/risk-fatca', this.corporateSeq], {
            queryParams: { clientSeq: this.selectedClientSeq },
          });
        }
        this.toastr.success(this.translate.instant('ONBOARDING.SAVED_SUCCESSFULLY'));
      },
      // ErrorInterceptor already toasts the backend's message (or a generic
      // fallback) for non-2xx responses, so no second toast here.
      error: () => {},
    });
  }
  // View mode mein bina save kiye next tab pe le jaye
  goToRiskFatca(): void {
    if (!this.corporateSeq) {
      this.toastr.error(this.translate.instant('ONB_CORPORATE.CORPORATE_DATA_MISSING'));
      return;
    }

    this.router.navigate(['/onboarding/risk-fatca', this.corporateSeq], {
      queryParams: {
        clientSeq: this.selectedClientSeq,
        mode: 'view', // 👈 view mode forward
      },
    });
  }
  // ================= PATCH FORM =================
  patchForm(res: any): void {
    // ✅ rebuild fields with latest options before patching

    setTimeout(() => {
      if (!this.dynamicForm?.form) return;

      this.dynamicForm.form.patchValue({
        nmOfInstitution: res.nmOfInstitution ?? null,
        incrprtnRegNmbr: res.incrprtnRegNmbr ?? null,
        dtOfIncrprtn: res.dtOfIncrprtn
          ? new Date(res.dtOfIncrprtn).toISOString().split('T')[0]
          : null,
        ntn: res.ntn ?? null,
        lglSts: res.lglSts ?? null,

        cmpny: res.cmpny ?? null,
        typ1: res.typ1 != null ? String(res.typ1) : null,
        typ2: res.typ2 != null ? String(res.typ2) : null,
        typeOfNbfcs: res.typNbfcs ?? null,
        regtrdAddrss: res.regtrdAddrss ?? null,
        stateSeq: res.stateSeq ?? null,
        cty: res.cty ?? null,
        rsdntlSts: res.rsdntlSts ?? null,
        mlngAddrss: res.mlngAddrss ?? null,
        mlngStateSeq: res.mlngStateSeq
          ? Number(res.mlngStateSeq)
          : res.mlngPrvnc
            ? Number(res.mlngPrvnc)
            : null,
        licenseNmbr: res.licenseNmbr ?? null,

        licenseIssueDt: res.licenseIssueDt
          ? new Date(res.licenseIssueDt).toISOString().split('T')[0]
          : null,
        mlngCty: res.mlngCty ?? null,
        cntctPrsnNm: res.cntctPrsnNm ?? null,
        dsgntn: res.dsgntn ?? null,
        mblNum: res.mblNum ?? null,
        emlAddrss: res.emlAddrss ?? null,
      });
      this.dynamicForm.form.get('nmOfInstitution')?.disable();
      this.institutionNameLocked = true;
      if (this.isViewMode) {
        this.dynamicForm.form.disable();
      }
    }, 100); // ✅ slight delay ensures DOM is ready
  }

  // ================= BUILD FIELDS =================
  buildFields(): void {
    this.fields = [
      {
        key: 'nmOfInstitution',
        label: 'ONB_CORPORATE.NAME_OF_INSTITUTION',
        type: 'text',
        validations: { required: true, textOnly: true },
      },
      {
        key: 'incrprtnRegNmbr',
        label: 'ONB_CORPORATE.INCORPORATION_REG_NO',
        type: 'text',
        inputmode: 'numeric',
        maxlength: 15,
        validations: {
          required: true,
          numbersOnly: true,
          minLength: 3,
          maxLength: 15,
        },
      },
      {
        key: 'dtOfIncrprtn',
        label: 'ONB_CORPORATE.DATE_OF_INCORPORATION',
        type: 'date',
        validations: { required: true, noFuturDate: true },
      },
      {
        key: 'ntn',
        label: 'ONB_CORPORATE.NTN',
        type: 'text',
        inputmode: 'numeric',
        maxlength: 13,
        validations: {
          required: true,
          numbersOnly: true,
          minLength: 7,
          maxLength: 13,
        },
      },
      {
        key: 'lglSts',
        label: 'ONB_CORPORATE.LEGAL_STATUS',
        type: 'select',
        options$: of(this.picklist.getOptionsSync(PKL_GRP.LEGAL_STATUS)),
        validations: { required: true },
      },
      {
        key: 'cmpny',
        label: 'ONB_CORPORATE.COMPANY',
        type: 'select',
        options$: of(this.picklist.getOptionsSync(PKL_GRP.GET_COMPANY)),
        validations: { required: true },
      },

      {
        key: 'typ1',
        label: 'ONB_CORPORATE.TYPE_1',
        type: 'radio',
        value: '1',
        options$: of([
          { label: 'ONB_CORPORATE.PUBLIC_LIMITED', value: '1' },
          { label: 'ONB_CORPORATE.PRIVATE_LIMITED', value: '0' },
        ]),
        validations: { required: true },
      },
      {
        key: 'typ2',
        label: 'ONB_CORPORATE.TYPE_2',
        type: 'radio',
        value: '1',
        options$: of([
          { label: 'ONB_CORPORATE.FOR_PROFIT', value: '1' },
          { label: 'ONB_CORPORATE.NOT_FOR_PROFIT', value: '0' },
        ]),
        validations: { required: true },
      },
      {
        key: 'typeOfNbfcs',
        label: 'ONB_CORPORATE.TYPE_OF_NBFCS',
        type: 'select',
        options$: of(this.picklist.getOptionsSync(PKL_GRP.TYPE_OF_NBFCS)),
        validations: { required: true },
      },
      {
        key: 'licenseNmbr',
        label: 'ONB_CORPORATE.LICENSE_NUMBER',
        type: 'text',
        maxlength: 15,
        validations: {
          required: true,
          minLength: 3,
          maxLength: 15,
        },
      },
      {
        key: 'licenseIssueDt',
        label: 'ONB_CORPORATE.LICENSE_ISSUE_DATE',
        type: 'date',
        validations: { required: true, noFuturDate: true },
      },
      {
        key: 'regtrdAddrss',
        label: 'ONB_CORPORATE.REGISTERED_ADDRESS',
        type: 'textarea',
        validations: { required: true },
      },

      {
        key: 'cty',
        label: 'ONB_PERSON.CITY',
        type: 'select',
        options$: of(this.cities),
        validations: { required: true },
      },
      {
        key: 'rsdntlSts',
        label: 'ONB_CORPORATE.RESIDENTIAL_STATUS',
        type: 'select',
        options$: of(this.picklist.getOptionsSync(PKL_GRP.RESIDENTIAL_STATUS)),
        validations: { required: true },
      },
      {
        key: 'mlngAddrss',
        label: 'ONB_CORPORATE.MAILING_ADDRESS',
        type: 'textarea',
        validations: { required: true },
      },
      {
        key: 'mlngStateSeq',
        label: 'ONB_CORPORATE.MAILING_STATE',
        type: 'select',
        options$: of(this.states),
        validations: { required: true },
      },
      {
        key: 'mlngCty',
        label: 'ONB_CORPORATE.MAILING_CITY',
        type: 'select',
        options$: of(this.cities),
        validations: { required: true },
      },
      {
        key: 'cntctPrsnNm',
        label: 'ONB_CORPORATE.CONTACT_PERSON_NAME',
        type: 'text',
        validations: { required: true, textOnly: true },
      },
      {
        key: 'dsgntn',
        label: 'ONB_PERSON.DESIGNATION',
        type: 'text',
        validations: { required: true, textOnly: true },
      },
      {
        key: 'mblNum',
        label: 'ONB_CORPORATE.MOBILE_NO',
        type: 'text',
        maxlength: 11,
        inputmode: 'numeric',
        validations: { required: true, phone: true },
      },
      {
        key: 'emlAddrss',
        label: 'ONB_CORPORATE.EMAIL_ADDRESS',
        type: 'email',
        validations: { required: true, email: true, knownEmailProviderDomain: true },
        errorMessages: {
          knownEmailProviderDomain: 'ONB_CORPORATE.EMAIL_PROVIDER_DOMAIN_ERROR',
        },
      },
    ];
  }

  // ================= SUBMIT =================
  submit(): void {
    if (this.isViewMode) return;

    if (this.dynamicForm.form.invalid) {
      this.dynamicForm.form.markAllAsTouched();
      this.toastr.error(this.translate.instant('ONBOARDING.PLEASE_FILL_REQUIRED_FIELDS'));
      return;
    }

    this.dynamicForm.submit();
  }

  // ================= PAYLOAD =================
  mapPayload(form: any): any {
    const rawForm = this.dynamicForm.form.getRawValue();

    return {
      crptOnbrdngSeq: this.corporateSeq ?? null,
      clntSeq: this.selectedClientSeq,
      nmOfInstitution: rawForm.nmOfInstitution,
      incrprtnRegNmbr: form.incrprtnRegNmbr,
      dtOfIncrprtn: form.dtOfIncrprtn,
      ntn: form.ntn,
      lglSts: form.lglSts,
      cmpny: form.cmpny,
      typ1: Number(form.typ1),
      typ2: Number(form.typ2),

      typNbfcs: form.typeOfNbfcs,

      regtrdAddrss: form.regtrdAddrss,
      cty: form.cty,
      rsdntlSts: form.rsdntlSts,
      mlngAddrss: form.mlngAddrss,
      mlngCty: form.mlngCty,
      mlngPrvnc: form.mlngStateSeq,
      cntctPrsnNm: form.cntctPrsnNm,
      dsgntn: form.dsgntn,
      mblNum: form.mblNum,
      emlAddrss: form.emlAddrss,

      licenseNmbr: form.licenseNmbr,
      licenseIssueDt: form.licenseIssueDt,

      stsKey: 699,
      delFlg: 0,
    };
  }
}
